import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api, type PlanVersion, type PlanHeadcountRow, type HeadcountStatus, type PlanCompEventRow, type CompEventType } from '../../lib/api';
import { Spinner, ActionButton, Modal, Badge } from '../ui';
import SeatTrajectory from './SeatTrajectory';
import { SaveStatus, useSaveState } from '../SaveStatus';

/**
 * Planning v2 People section — headcount + comp events in the Budget-Grid idiom:
 * a clean row grid with a centered popup editor (add/edit), consistent styling,
 * and a toolbar for the bulk actions (import from users, apply/clear capacity).
 * Reuses the existing headcount / comp-event endpoints.
 */

const STATUSES: HeadcountStatus[] = ['planned', 'filled', 'open', 'attrited'];
const EVENT_TYPES: CompEventType[] = ['merit', 'promotion', 'bonus_accrual', 'equity_refresh', 'one_time'];
const money = (n: number | null | undefined) => (n == null ? '' : n.toLocaleString(undefined, { maximumFractionDigits: 0 }));
const pct = (n: number | null | undefined) => (n == null ? '' : `${+(n * 100).toFixed(1)}%`);

const box: React.CSSProperties = { fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)' };
const th: React.CSSProperties = { padding: '4px 8px', fontSize: 11, fontWeight: 600, textAlign: 'left', borderBottom: '0.5px solid var(--color-border)', color: 'var(--color-text-muted)' };
const td: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)' };
const num = (v: string) => (v.trim() === '' ? null : Number(v));

export default function PlanPeopleGrid({ plan, canWrite }: { plan: PlanVersion; canWrite: boolean }) {
  const planId = plan.id;
  const hcQ = useQuery(() => api.fpa.plans.headcount(planId), [planId]);
  const ceQ = useQuery(() => api.fpa.plans.compEvents(planId), [planId]);
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);
  const rampsQ = useQuery(() => api.fpa.rampCurves(), [planId]);
  // Who can be seated. Same pool 'Import from users' draws from.
  const rosterQ = useQuery(() => api.fpa.departmentRoster(), [planId]);

  const save = useSaveState();
  const [hcEdit, setHcEdit] = useState<PlanHeadcountRow | 'new' | null>(null);
  const [ceEdit, setCeEdit] = useState<PlanCompEventRow | 'new' | null>(null);
  const [traj, setTraj] = useState<PlanHeadcountRow | null>(null);
  // Whole-plan trajectory rows, only so the table can say which seats have
  // one. The editor fetches the seat's own rows when it opens.
  const trajQ = useQuery(() => api.fpa.plans.headcountSchedule(planId), [planId]);

  if (hcQ.isLoading || ceQ.isLoading) return <Spinner />;
  const headcount = hcQ.data?.headcount ?? [];
  const comp = ceQ.data?.comp_events ?? [];
  const departments = deptsQ.data?.departments ?? [];
  const ramps = rampsQ.data?.ramp_curves ?? [];
  const roster = rosterQ.data?.members ?? [];
  const deptName = (id: string | null) => departments.find((d) => d.id === id)?.name ?? id ?? '—';
  // Distinct months with a trajectory row, per seat: enough to tell a seat
  // that has been shaped from one running on its flat scalar.
  const trajCount = new Map<string, number>();
  for (const [id, months] of (() => {
    const acc = new Map<string, Set<string>>();
    for (const r of trajQ.data?.schedule ?? []) {
      (acc.get(r.headcount_id) ?? acc.set(r.headcount_id, new Set()).get(r.headcount_id)!).add(r.period_month);
    }
    return acc;
  })()) trajCount.set(id, months.size);

  // These endpoints recompute the plan in the same transaction as the write,
  // so there is no follow-up recompute call to make.
  async function withSave(fn: () => Promise<unknown>, note?: string) {
    if (await save.run(fn, note)) { hcQ.refetch(); ceQ.refetch(); }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Headcount</span>
        {canWrite && <>
          <ActionButton onClick={() => setHcEdit('new')}>+ Add role</ActionButton>
          <ActionButton onClick={() => withSave(() => api.fpa.plans.importHeadcountFromUsers(planId), 'staff imported from users')} disabled={save.busy}>Import from users</ActionButton>
          <ActionButton onClick={() => withSave(() => api.fpa.plans.applyCapacity(planId), 'capacity applied from history')} disabled={save.busy}>Apply capacity</ActionButton>
          <ActionButton onClick={() => withSave(() => api.fpa.plans.clearCapacity(planId), 'capacity numbers cleared')} disabled={save.busy}>Clear capacity</ActionButton>
        </>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <SaveStatus state={save} />
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead><tr>
            <th style={th}>Role</th><th style={th}>Person</th><th style={th}>Dept</th><th style={th}>Status</th><th style={{ ...th, textAlign: 'center' }}>Billable</th>
            <th style={th}>Window</th><th style={{ ...th, textAlign: 'right' }}>Base salary</th><th style={{ ...th, textAlign: 'right' }}>Loaded ×</th>
            <th style={{ ...th, textAlign: 'right' }}>Util</th><th style={{ ...th, textAlign: 'right' }}>Hours</th><th style={{ ...th, textAlign: 'right' }}>Rate</th><th style={th}>Ramp</th>
            <th style={th}>Trajectory</th>
          </tr></thead>
          <tbody>
            {headcount.map((h) => (
              <tr key={h.id} style={{ cursor: canWrite ? 'pointer' : 'default' }} onClick={() => canWrite && setHcEdit(h)}>
                <td style={td}>{h.role_label}</td>
                <td style={td}>{h.linked_user_name
                  ? h.linked_user_name
                  : <span style={{ color: 'var(--color-text-muted)' }}>— unassigned —</span>}</td>
                <td style={td}>{deptName(h.department_id)}</td>
                <td style={td}><Badge variant={h.status === 'filled' ? 'green' : h.status === 'planned' ? 'blue' : h.status === 'open' ? 'amber' : 'gray'}>{h.status}</Badge></td>
                <td style={{ ...td, textAlign: 'center' }}>{h.is_billable ? '✓' : ''}</td>
                <td style={td}>{h.start_month}{h.end_month ? ` → ${h.end_month}` : ''}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(h.annual_base_salary)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{h.fully_loaded_multiplier}</td>
                <td style={{ ...td, textAlign: 'right' }}>{pct(h.target_utilisation)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{h.target_hours_per_head ?? ''}</td>
                <td style={{ ...td, textAlign: 'right' }}>{money(h.target_bill_rate)}</td>
                <td style={td}>{h.ramp_curve_id ?? ''}</td>
                {/* The row's own onClick opens the seat editor, so this has to
                    stop propagating or both dialogs fight over the click. */}
                <td style={td} onClick={(e) => { e.stopPropagation(); setTraj(h); }}>
                  <ActionButton onClick={() => setTraj(h)}
                    title="Month-by-month utilisation, productivity, bill rate, hours and salary for this person - ramp up, taper down, or a stretch of leave.">
                    {trajCount.get(h.id) ? `${trajCount.get(h.id)} months` : 'Set…'}
                  </ActionButton>
                </td>
              </tr>
            ))}
            {headcount.length === 0 && <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={13}>No headcount yet — add a role or import from users.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '14px 12px 8px', borderTop: '0.5px solid var(--color-border)' }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Comp events</span>
        {canWrite && <ActionButton onClick={() => setCeEdit('new')}>+ Add event</ActionButton>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead><tr><th style={th}>Effective</th><th style={th}>Type</th><th style={th}>Applies to</th><th style={{ ...th, textAlign: 'right' }}>%</th><th style={{ ...th, textAlign: 'right' }}>$</th><th style={th}>Notes</th></tr></thead>
          <tbody>
            {comp.map((c) => (
              <tr key={c.id} style={{ cursor: canWrite ? 'pointer' : 'default' }} onClick={() => canWrite && setCeEdit(c)}>
                <td style={td}>{c.effective_month}</td>
                <td style={td}>{c.event_type}</td>
                <td style={td}>{c.headcount_id ? (headcount.find((h) => h.id === c.headcount_id)?.role_label ?? 'seat') : deptName(c.department_id)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{pct(c.amount_pct)}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(c.amount_value)}</td>
                <td style={{ ...td, color: 'var(--color-text-muted)' }}>{c.notes}</td>
              </tr>
            ))}
            {comp.length === 0 && <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={6}>No comp events.</td></tr>}
          </tbody>
        </table>
      </div>

      {hcEdit && <HeadcountEditor planId={planId} row={hcEdit === 'new' ? undefined : hcEdit} departments={departments} ramps={ramps} roster={roster} headcount={headcount} onClose={() => setHcEdit(null)} onSaved={() => { setHcEdit(null); save.markSaved('role saved'); hcQ.refetch(); ceQ.refetch(); }} onDelete={hcEdit !== 'new' ? () => { const id = hcEdit.id; setHcEdit(null); withSave(() => api.fpa.plans.deleteHeadcount(planId, id), 'role deleted'); } : undefined} />}
      {traj && (
        <SeatTrajectory
          plan={plan} seat={traj} canWrite={canWrite}
          onClose={() => setTraj(null)}
          onChanged={() => { trajQ.refetch(); hcQ.refetch(); }}
        />
      )}
      {ceEdit && <CompEventEditor planId={planId} row={ceEdit === 'new' ? undefined : ceEdit} departments={departments} headcount={headcount} onClose={() => setCeEdit(null)} onSaved={() => { setCeEdit(null); save.markSaved('comp event saved'); hcQ.refetch(); ceQ.refetch(); }} onDelete={ceEdit !== 'new' ? () => { const id = ceEdit.id; setCeEdit(null); withSave(() => api.fpa.plans.deleteCompEvent(planId, id), 'comp event deleted'); } : undefined} />}
    </div>
  );
}

const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
const lab: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)', width: 120 };

function HeadcountEditor({ planId, row: r, departments, ramps, roster, headcount, onClose, onSaved, onDelete }: {
  planId: string; row?: PlanHeadcountRow; departments: Array<{ id: string; name: string }>; ramps: Array<{ id: string; name: string }>;
  roster: Array<{ jira_user_id: string; display_name: string; title: string; department_id: string }>;
  headcount: PlanHeadcountRow[];
  onClose: () => void; onSaved: () => void; onDelete?: () => void;
}) {
  const [f, setF] = useState({
    role_label: r?.role_label ?? '', department_id: r?.department_id ?? (departments[0]?.id ?? ''),
    is_billable: r?.is_billable ?? true, status: r?.status ?? 'planned' as HeadcountStatus,
    start_month: r?.start_month ?? '', end_month: r?.end_month ?? '',
    annual_base_salary: r ? String(r.annual_base_salary) : '', fully_loaded_multiplier: r ? String(r.fully_loaded_multiplier) : '1.25',
    target_utilisation: r?.target_utilisation != null ? String(+(r.target_utilisation * 100).toFixed(2)) : '',
    target_hours_per_head: r?.target_hours_per_head != null ? String(r.target_hours_per_head) : '',
    target_bill_rate: r?.target_bill_rate != null ? String(r.target_bill_rate) : '',
    ramp_curve_id: r?.ramp_curve_id ?? '', notes: r?.notes ?? '',
    linked_user_id: r?.linked_user_id ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.role_label.trim() || !f.department_id || !/^\d{4}-\d{2}$/.test(f.start_month)) { setErr('Role, department and start month (YYYY-MM) are required'); return; }
    setBusy(true); setErr(null);
    try {
      await api.fpa.plans.upsertHeadcount(planId, {
        ...(r ? { id: r.id } : {}),
        role_label: f.role_label.trim(), department_id: f.department_id, is_billable: f.is_billable, status: f.status,
        start_month: f.start_month, end_month: f.end_month || null,
        annual_base_salary: Number(f.annual_base_salary || 0), fully_loaded_multiplier: Number(f.fully_loaded_multiplier || 1.25),
        target_utilisation: f.target_utilisation === '' ? null : Number(f.target_utilisation) / 100,
        target_hours_per_head: num(f.target_hours_per_head), target_bill_rate: num(f.target_bill_rate),
        ramp_curve_id: f.ramp_curve_id || null, notes: f.notes || null,
        linked_user_id: f.linked_user_id || null,
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Modal title={r ? 'Edit role' : 'Add role'} onClose={onClose} width={540}>
      <div style={{ padding: 20 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}><span style={lab}>Role label</span><input autoFocus value={f.role_label} onChange={(e) => set('role_label', e.target.value)} style={{ ...box, flex: 1 }} /></div>
        <div style={row}><span style={lab}>Person in seat</span>
          <select value={f.linked_user_id} onChange={(e) => set('linked_user_id', e.target.value)} style={{ ...box, flex: 1 }}>
            <option value="">— unassigned —</option>
            {/* A seat held by someone no longer on the active roster still has to
                round-trip, or saving would silently empty the seat. */}
            {r?.linked_user_id && !roster.some((m) => m.jira_user_id === r.linked_user_id) &&
              <option value={r.linked_user_id}>{r.linked_user_name ?? r.linked_user_id} (not on the active roster)</option>}
            {roster.map((m) => {
              const otherSeat = headcount.find((h) => h.linked_user_id === m.jira_user_id && h.id !== r?.id);
              return <option key={m.jira_user_id} value={m.jira_user_id}>
                {m.display_name}{m.title ? ` — ${m.title}` : ''}{otherSeat ? ` (already in "${otherSeat.role_label}")` : ''}
              </option>;
            })}
          </select></div>
        <div style={row}><span style={lab}>Department</span><select value={f.department_id} onChange={(e) => set('department_id', e.target.value)} style={{ ...box, flex: 1 }}>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
        <div style={row}><span style={lab}>Status</span><select value={f.status} onChange={(e) => set('status', e.target.value)} style={box}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          <label style={{ fontSize: 11, display: 'flex', gap: 4, alignItems: 'center', marginLeft: 12 }}><input type="checkbox" checked={f.is_billable} onChange={(e) => set('is_billable', e.target.checked)} /> Billable</label></div>
        <div style={row}><span style={lab}>Window</span><input value={f.start_month} onChange={(e) => set('start_month', e.target.value)} placeholder="start YYYY-MM" style={{ ...box, width: 120 }} /><input value={f.end_month} onChange={(e) => set('end_month', e.target.value)} placeholder="end (optional)" style={{ ...box, width: 120 }} /></div>
        <div style={row}><span style={lab}>Base salary (yr)</span><input value={f.annual_base_salary} onChange={(e) => set('annual_base_salary', e.target.value)} style={{ ...box, width: 140 }} /><span style={lab}>Loaded ×</span><input value={f.fully_loaded_multiplier} onChange={(e) => set('fully_loaded_multiplier', e.target.value)} style={{ ...box, width: 70 }} /></div>
        <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginBottom: 6 }}>Per-seat capacity overrides (blank = fall back to the department driver):</div>
        <div style={row}><span style={lab}>Utilisation %</span><input value={f.target_utilisation} onChange={(e) => set('target_utilisation', e.target.value)} style={{ ...box, width: 80 }} /><span style={lab}>Hours/mo</span><input value={f.target_hours_per_head} onChange={(e) => set('target_hours_per_head', e.target.value)} style={{ ...box, width: 70 }} /><span style={lab}>Rate</span><input value={f.target_bill_rate} onChange={(e) => set('target_bill_rate', e.target.value)} style={{ ...box, width: 80 }} /></div>
        <div style={row}><span style={lab}>Ramp curve</span><select value={f.ramp_curve_id} onChange={(e) => set('ramp_curve_id', e.target.value)} style={box}><option value="">— none —</option>{ramps.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <ActionButton variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : r ? 'Save' : 'Add'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          {onDelete && <span style={{ marginLeft: 'auto' }}><ActionButton variant="danger" onClick={onDelete}>Delete</ActionButton></span>}
        </div>
      </div>
    </Modal>
  );
}

function CompEventEditor({ planId, row: r, departments, headcount, onClose, onSaved, onDelete }: {
  planId: string; row?: PlanCompEventRow; departments: Array<{ id: string; name: string }>; headcount: PlanHeadcountRow[];
  onClose: () => void; onSaved: () => void; onDelete?: () => void;
}) {
  const [f, setF] = useState({
    effective_month: r?.effective_month ?? '', event_type: r?.event_type ?? 'merit' as CompEventType,
    scope: r?.headcount_id ? 'seat' : 'dept', headcount_id: r?.headcount_id ?? '', department_id: r?.department_id ?? (departments[0]?.id ?? ''),
    amount_pct: r?.amount_pct != null ? String(+(r.amount_pct * 100).toFixed(2)) : '', amount_value: r?.amount_value != null ? String(r.amount_value) : '', notes: r?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: unknown) => setF((p) => ({ ...p, [k]: v }));
  const usesPct = f.event_type === 'merit' || f.event_type === 'promotion';

  async function save() {
    if (!/^\d{4}-\d{2}$/.test(f.effective_month)) { setErr('Effective month (YYYY-MM) is required'); return; }
    setBusy(true); setErr(null);
    try {
      await api.fpa.plans.upsertCompEvent(planId, {
        ...(r ? { id: r.id } : {}),
        effective_month: f.effective_month, event_type: f.event_type,
        headcount_id: f.scope === 'seat' ? (f.headcount_id || null) : null,
        department_id: f.scope === 'dept' ? f.department_id : null,
        amount_pct: usesPct ? (f.amount_pct === '' ? null : Number(f.amount_pct) / 100) : null,
        amount_value: !usesPct ? num(f.amount_value) : null,
        notes: f.notes || null,
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <Modal title={r ? 'Edit comp event' : 'Add comp event'} onClose={onClose} width={520}>
      <div style={{ padding: 20 }}>
        {err && <p style={{ color: 'var(--color-danger, #c0392b)', fontSize: 12 }}>{err}</p>}
        <div style={row}><span style={lab}>Effective month</span><input autoFocus value={f.effective_month} onChange={(e) => set('effective_month', e.target.value)} placeholder="YYYY-MM" style={{ ...box, width: 120 }} /></div>
        <div style={row}><span style={lab}>Type</span><select value={f.event_type} onChange={(e) => set('event_type', e.target.value)} style={box}>{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
        <div style={row}><span style={lab}>Applies to</span>
          <select value={f.scope} onChange={(e) => set('scope', e.target.value)} style={box}><option value="dept">Department</option><option value="seat">Specific seat</option></select>
          {f.scope === 'dept'
            ? <select value={f.department_id} onChange={(e) => set('department_id', e.target.value)} style={box}>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
            : <select value={f.headcount_id} onChange={(e) => set('headcount_id', e.target.value)} style={box}><option value="">— select —</option>{headcount.map((h) => <option key={h.id} value={h.id}>{h.role_label}</option>)}</select>}
        </div>
        {usesPct
          ? <div style={row}><span style={lab}>Amount %</span><input value={f.amount_pct} onChange={(e) => set('amount_pct', e.target.value)} style={{ ...box, width: 90 }} /><span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>compounds base from this month</span></div>
          : <div style={row}><span style={lab}>Amount $</span><input value={f.amount_value} onChange={(e) => set('amount_value', e.target.value)} style={{ ...box, width: 120 }} /><span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{f.event_type === 'bonus_accrual' ? 'spread from this month' : 'lands in this month'}</span></div>}
        <div style={row}><span style={lab}>Notes</span><input value={f.notes} onChange={(e) => set('notes', e.target.value)} style={{ ...box, flex: 1 }} /></div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <ActionButton variant="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : r ? 'Save' : 'Add'}</ActionButton>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          {onDelete && <span style={{ marginLeft: 'auto' }}><ActionButton variant="danger" onClick={onDelete}>Delete</ActionButton></span>}
        </div>
      </div>
    </Modal>
  );
}
